import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Typography, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import { Form } from '@formio/react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

// The forms for a person scanned in at a Service Provider event, filled and edited as on mobile.
// The forms offered are the event task type's; a saved row's form_type is the fw_forms id.
function EventAttendeeFormsModal(props) {
  const { schedule, attendee } = props;
  const [forms, setForms] = useState([]);
  const [savedForms, setSavedForms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // { form, saved, submission } for the form open in the dialog; saved is its row, if filled.
  const [viewedForm, setViewedForm] = useState(null);
  const [saveError, setSaveError] = useState('');
  const isSavingRef = useRef(false);

  const loadSavedForms = async () => {
    const saved = await axios.get(APIURL + '/session_attendee/' + attendee.attendee_id + '/forms', { params: { company_id: props.comp_id } });
    setSavedForms(Array.isArray(saved.data) ? saved.data : []);
  };

  useEffect(() => {
    setForms([]);
    setSavedForms([]);
    setViewedForm(null);
    if (!props.open || !schedule || !attendee) {
      return;
    }

    const loadForms = async () => {
      setIsLoading(true);
      try {
        const [taskTypes] = await Promise.all([
          axios.get(APIURL + '/dropdowns/dropdownType/fullTaskType', { params: { comp_id: props.comp_id } }),
          loadSavedForms(),
        ]);
        const taskTypeId = taskTypes.data.find(type => type.EntityValue === schedule.task_type)?.ddID;
        const taskForms = taskTypeId ? await axios.get(APIURL + '/task_form', { params: { taskType_id: taskTypeId, company_id: props.comp_id } }) : { data: [] };
        setForms(taskForms.data);
      } catch (error) {
        console.log(error);
        props.onError('Error fetching attendee forms');
      } finally {
        setIsLoading(false);
      }
    };
    loadForms();
  }, [props.open, attendee?.attendee_id]);

  const findSaved = (form) => savedForms.find(saved => String(saved.form_type) === String(form.form_id));

  const submissionOf = (saved) => {
    try {
      return saved ? JSON.parse(saved.data) : {};
    } catch (error) {
      return {};
    }
  };

  const openForm = (form) => {
    const saved = findSaved(form);
    setSaveError('');
    setViewedForm({ form, saved, submission: submissionOf(saved) });
  };

  const handleSubmit = async (submission) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setSaveError('');
    const body = { data: JSON.stringify(submission), company_id: props.comp_id, emp_id: props.user.emp_id };
    try {
      let attendeeFormId = viewedForm.saved?.attendee_form_id;
      if (!attendeeFormId) {
        try {
          await axios.post(APIURL + '/session_attendee/' + attendee.attendee_id + '/forms', { ...body, form_type: viewedForm.form.form_id });
        } catch (error) {
          // Filled meanwhile, e.g. on mobile: update that row instead.
          if (error.response?.status !== 409) throw error;
          attendeeFormId = error.response.data.attendee_form_id;
        }
      }
      if (attendeeFormId) {
        await axios.put(APIURL + '/session_attendee/forms/' + attendeeFormId, body);
      }
      setViewedForm(null);
      await loadSavedForms();
      if (props.onFormsUpdated) props.onFormsUpdated();
    } catch (error) {
      console.log(error);
      setSaveError(error.response?.data?.message || 'Unable to save form. Please retry.');
    } finally {
      isSavingRef.current = false;
    }
  };

  return (
    <Box>
      <Modal enforceFocus={false}
        open={props.open}
        onClose={props.onClose}
        aria-labelledby="event-attendee-forms-modal-title"
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 500, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '90vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography id="event-attendee-forms-modal-title" variant="h6" component="h2" sx={{ wordBreak: 'break-all' }}>
              {attendee?.barcode_value}
            </Typography>
            <IconButton aria-label="close" color="inherit" sx={{ p: 0.25, alignSelf: 'flex-start' }} onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Scanned {moment(attendee?.scanned_on).format('MM/DD/YYYY hh:mm A')}{attendee?.scanned_by_name ? ' by ' + attendee.scanned_by_name : ''}
          </Typography>
          <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
            Forms:
          </Typography>
          {isLoading ? (
            <CircularProgress size={24} sx={{ display: 'block', margin: '16px auto' }} />
          ) : forms.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No forms for this event.</Typography>
          ) : forms.map(form => {
            const saved = findSaved(form);
            return (
              <Box
                key={form.form_id}
                onClick={() => openForm(form)}
                sx={{ border: '1px solid black', borderRadius: '5px', p: '5px', m: '5px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
              >
                <Box>{form.form_name}</Box>
                {saved ? <CheckCircleOutlinedIcon /> : null}
              </Box>
            );
          })}
        </Box>
      </Modal>
      {/* Closes only via the Close button, so a stray click can't discard input */}
      <Dialog open={!!viewedForm} maxWidth="md" fullWidth scroll="body" disableRestoreFocus>
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
            {viewedForm?.form.form_name} <span style={{ fontWeight: 'normal' }}>for</span> {attendee?.barcode_value}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {viewedForm && (
            <Form
              key={viewedForm.form.form_id}
              form={JSON.parse(viewedForm.form.translated_json_schema || viewedForm.form.json_schema)}
              submission={viewedForm.submission}
              onSubmit={handleSubmit}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px 16px' }}>
          <Button variant="contained" color="error" onClick={() => setViewedForm(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EventAttendeeFormsModal;
